import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
    const [auth, setAuth] = useState({ loading: true, user: null, tenant: null, companies: [], activeCompanyId: null });

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            const stored = localStorage.getItem("active_company_id");
            const validIds = (data.companies || []).map((c) => c.id);
            const active = stored && validIds.includes(stored) ? stored : (data.user?.default_company_id || validIds[0] || null);
            if (active) localStorage.setItem("active_company_id", active);
            if (data.user) data.user.__rbac = data.rbac || null; // dynamic module map for canSee()
            setAuth({ loading: false, user: data.user, tenant: data.tenant, companies: data.companies || [], activeCompanyId: active });
        } catch {
            setAuth({ loading: false, user: null, tenant: null, companies: [], activeCompanyId: null });
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        // Account has two-factor on — hand the challenge back to the caller;
        // no session is issued until the second factor is completed.
        if (data.mfa_required) return data;
        if (data.access_token) localStorage.setItem("access_token", data.access_token);
        await refresh();
        return data;
    };

    const loginMfa = async (mfaToken, code) => {
        const { data } = await api.post("/auth/login/mfa", { mfa_token: mfaToken, code });
        if (data.access_token) localStorage.setItem("access_token", data.access_token);
        await refresh();
        return data;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch {}
        localStorage.removeItem("access_token");
        localStorage.removeItem("active_company_id");
        setAuth({ loading: false, user: null, tenant: null, companies: [], activeCompanyId: null });
    };

    const switchCompany = (companyId) => {
        localStorage.setItem("active_company_id", companyId);
        setAuth((a) => ({ ...a, activeCompanyId: companyId }));
    };

    return (
        <AuthCtx.Provider value={{ ...auth, login, loginMfa, logout, switchCompany, refresh }}>
            {children}
        </AuthCtx.Provider>
    );
};

export const useAuth = () => useContext(AuthCtx);
